/**
 * useHls - the playback engine.
 *
 * Owns one <video> element per feed slot and, where needed, one Hls.js
 * instance per slot. hls.js is loaded lazily (dynamic import) so it never
 * blocks first paint. Native HLS (Safari) is used directly when available.
 *
 * Responsibilities:
 *   - attach/detach sources idempotently (`ensureSource`)
 *   - play with autoplay-policy fallback to muted (`playVideo`)
 *   - report download progress + fatal errors back to the feed
 *   - tear instances down to keep memory bounded
 */
import { useRef, useEffect } from 'preact/hooks';

export function useHls() {
  const videoRefs = useRef([]); // HTMLVideoElement[]
  const hlsRefs = useRef([]); // Hls[]
  const attachedSrc = useRef([]); // currently attached playlist per slot
  const requestedSrc = useRef([]); // last requested playlist per slot
  const modulePromise = useRef(null);

  /** Lazy-load hls.js once. */
  function preloadModule() {
    if (!modulePromise.current) {
      modulePromise.current = import('hls.js').then((m) => ({
        Hls: m.default,
      }));
    }
    return modulePromise.current;
  }

  const canPlayNativeHls = (el) =>
    !!(el && el.canPlayType('application/vnd.apple.mpegurl'));

  const isHlsActive = (i) => !!hlsRefs.current[i];
  const maxSlotCount = () =>
    Math.max(videoRefs.current.length, hlsRefs.current.length);
  const hasAnyRef = (i) => !!videoRefs.current[i] || !!hlsRefs.current[i];

  /** Detach + free a single slot's source. */
  function cleanupSource(i) {
    requestedSrc.current[i] = '';
    const hls = hlsRefs.current[i];
    if (hls) {
      hls.destroy();
      hlsRefs.current[i] = null;
      attachedSrc.current[i] = '';
    }
    const el = videoRefs.current[i];
    if (el && el.src) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
  }

  function cleanupAll() {
    hlsRefs.current.forEach((h) => h?.destroy());
    hlsRefs.current = [];
    attachedSrc.current = [];
    videoRefs.current = [];
    requestedSrc.current = [];
  }

  /**
   * Ensure slot `index` is wired to `playlistUrl`. Safe to call repeatedly:
   * it no-ops when the source is already attached and bails out if the slot
   * was reassigned while hls.js was loading (the dynamic import is async).
   */
  async function ensureSource({ index, playlistUrl, onLoadProgress, onFatalError }) {
    const el = videoRefs.current[index];
    if (!el || !playlistUrl) return;
    requestedSrc.current[index] = playlistUrl;

    const { Hls } = await preloadModule();

    if (Hls?.isSupported?.()) {
      // The slot/source may have changed during the await.
      if (requestedSrc.current[index] !== playlistUrl || videoRefs.current[index] !== el) {
        return;
      }
      const existing = hlsRefs.current[index];
      if (existing) {
        if (attachedSrc.current[index] === playlistUrl) return;
        existing.destroy();
      }
      const hls = new Hls({
        xhrSetup: (xhr, url) => {
          if (url.includes('/playlist/')) return;
          xhr.addEventListener('progress', (e) => {
            if (!e.lengthComputable || !e.total) return;
            const pct = Math.min(
              100,
              Math.max(0, Math.round((e.loaded / e.total) * 100)),
            );
            onLoadProgress?.(pct);
          });
        },
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          onFatalError?.({
            type: data.type,
            details: data.details,
            reason: data.reason || data.error?.message || null,
          });
          try {
            hls.destroy();
          } catch {
            /* noop */
          }
        }
      });
      hls.loadSource(playlistUrl);
      hls.attachMedia(el);
      hlsRefs.current[index] = hls;
      attachedSrc.current[index] = playlistUrl;
      return;
    }

    // Native HLS path (Safari / iOS).
    if (canPlayNativeHls(el) && el.src !== playlistUrl) {
      cleanupSource(index);
      requestedSrc.current[index] = playlistUrl;
      el.src = playlistUrl;
      el.load();
    }
  }

  /**
   * Play slot `index`. On a blocked autoplay (NotAllowedError) we retry muted
   * and notify via `onMutedFallback`. Returns whether playback started.
   */
  async function playVideo(index, { markPaused, onMutedFallback } = {}) {
    const el = videoRefs.current[index];
    if (!el) return false;
    try {
      await el.play();
      return true;
    } catch (err) {
      if (err?.name === 'NotAllowedError' && !el.muted) {
        onMutedFallback?.();
        el.muted = true;
        try {
          await el.play();
          return true;
        } catch {
          /* fallthrough */
        }
      }
      markPaused?.(index);
      console.error('Play error:', err);
      return false;
    }
  }

  // Free every Hls instance on unmount.
  useEffect(
    () => () => {
      hlsRefs.current.forEach((h) => h?.destroy());
    },
    [],
  );

  return {
    videoRefs,
    preloadModule,
    isHlsActive,
    maxSlotCount,
    hasAnyRef,
    cleanupSource,
    cleanupAll,
    ensureSource,
    playVideo,
  };
}
